/**
 * Input Component V2
 *
 * WCAG 2.1 Level AA + BFSG konform
 * Siehe: docs/frontend/design-rules.md (Tokens: docs/frontend/design-contract.md)
 */

import React, { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface InputProps extends ComponentPropsWithoutRef<'input'> {
  /**
   * Input label (required for accessibility)
   */
  label?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Helper text
   */
  helperText?: string;

  /**
   * Icon on the left side
   */
  iconLeft?: React.ReactNode;

  /**
   * Icon on the right side
   */
  iconRight?: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Input Component
 *
 * Features:
 * - WCAG 2.1 AA compliant (4.5:1 contrast ratios)
 * - BFSG compliant (44x44px minimum touch target)
 * - Associated label for screen readers
 * - Error state with aria-invalid and aria-describedby
 * - Helper text support
 * - Icon support (left/right)
 * - Dark/Light mode support
 *
 * @example
 * ```tsx
 * // Basic input
 * <Input
 *   label="E-Mail"
 *   type="email"
 *   placeholder="name@example.com"
 * />
 *
 * // With error
 * <Input
 *   label="Passwort"
 *   type="password"
 *   error="Passwort muss mindestens 8 Zeichen lang sein"
 * />
 *
 * // With icon
 * <Input
 *   label="Suche"
 *   iconLeft={<Search />}
 *   placeholder="Produkte suchen..."
 * />
 * ```
 */
export const Input = forwardRef<ElementRef<'input'>, InputProps>(
  (
    {
      label,
      error,
      helperText,
      iconLeft,
      iconRight,
      className,
      id,
      type = 'text',
      disabled,
      required,
      ...props
    },
    ref
  ) => {
    // Generate unique ID if not provided
    const { t } = useTranslation();
    const generatedId = React.useId();
    const inputId = id || `input-${generatedId}`;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    // Determine if input has error
    const hasError = Boolean(error);

    return (
      <div className="w-full">
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'block text-sm font-medium mb-1.5',
              'text-neutral-700 dark:text-neutral-300',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {label}
            {required && (
              <span className="text-red-600 dark:text-red-400 ml-1" aria-label={t('common.required')}>
                *
              </span>
            )}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Icon Left */}
          {iconLeft && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 pointer-events-none">
              {iconLeft}
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            type={type}
            disabled={disabled}
            required={required}
            className={cn(
              // Base styles
              'flex w-full rounded-md border',
              'bg-white dark:bg-neutral-900 px-3 py-2',
              'text-sm text-neutral-900 dark:text-neutral-100',

              // BFSG: Touch targets minimum 44x44px (h-11 = 44px)
              'min-h-11',

              // Placeholder
              'placeholder:text-neutral-400 dark:placeholder:text-neutral-500',

              // Focus styles (WCAG 2.1: 3:1 contrast minimum)
              'focus-visible:outline-none',
              'focus-visible:ring-2 focus-visible:ring-offset-2',
              'focus-visible:ring-primary-500 dark:focus-visible:ring-offset-neutral-900',

              // Disabled state
              'disabled:cursor-not-allowed disabled:opacity-50',

              // Border color
              hasError
                ? 'border-red-600 dark:border-red-500 focus-visible:ring-red-600 dark:focus-visible:ring-red-500'
                : 'border-neutral-300 dark:border-neutral-700',

              // Icon padding
              iconLeft && 'pl-10',
              iconRight && 'pr-10',

              // Custom className
              className
            )}
            // ARIA attributes for accessibility
            aria-invalid={hasError}
            aria-describedby={cn(error && errorId, helperText && helperId)}
            aria-required={required}
            {...props}
          />

          {/* Icon Right */}
          {iconRight && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 pointer-events-none">
              {iconRight}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p
            id={errorId}
            className="mt-1.5 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}

        {/* Helper Text */}
        {!error && helperText && (
          <p
            id={helperId}
            className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
